import React, { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Modal, Spin, Tag, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import s from './s.module.less';
import useOpsGroupDetail from 'hooks/useOpsGroupDetail';
import { updateGPListingStatus } from 'api/operation/group';
import GroupPracticeTab from 'pages/operation/PPAProviderDetailPage/components/GroupPracticeTab';
import KlarityProfileTab from './components/KlarityProfileTab';
import { isDevEnv, isTestEnv } from 'constants/common';

const getHelloKlarityHost = () => {
    if (isDevEnv()) return 'https://helloklarity.klarity-dev.boxpractice.com';
    if (isTestEnv()) return 'https://helloklarity.klarity-test.boxpractice.com';
    return 'https://www.helloklarity.com';
};

const TAB_KEY = 'tab';
const TAB_KLARITY = 'klarity-profile';
const TAB_GP = 'group-practice';

enum ETab {
    KLARITY_PROFILE = 'Klarity profile',
    GROUP_PRACTICE = 'Group practice',
}

const TAB_URL_MAP: Record<string, ETab> = {
    [TAB_KLARITY]: ETab.KLARITY_PROFILE,
    [TAB_GP]: ETab.GROUP_PRACTICE,
};

const TAB_TO_URL: Record<ETab, string> = {
    [ETab.KLARITY_PROFILE]: TAB_KLARITY,
    [ETab.GROUP_PRACTICE]: TAB_GP,
};

const GP_STATUS_MAP: Record<string, { color: string; label: string }> = {
    INIT: { color: 'gold', label: 'Onboarding' },
    LISTED: { color: 'green', label: 'Active' },
    PAUSE_LISTED: { color: 'orange', label: 'Paused' },
    UNLISTED: { color: 'default', label: 'Unlisted' },
    PENDING_REVIEW: { color: 'gold', label: 'Pending review' },
    ONBOARDING: { color: 'blue', label: 'Onboarding' },
};

const GroupPracticeDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { gpId } = useParams<{ gpId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [detail, loading, refetch] = useOpsGroupDetail(gpId ? Number(gpId) : undefined);

    const isListed = detail?.listingStatus === 'LISTED';
    const showResumeListing = detail?.listingStatus === 'UNLISTED' || detail?.listingStatus === 'PAUSE_LISTED';

    const tabFromUrl = searchParams.get(TAB_KEY);
    const defaultTab = isListed ? ETab.KLARITY_PROFILE : ETab.GROUP_PRACTICE;
    const currentTab = (tabFromUrl && TAB_URL_MAP[tabFromUrl]) || defaultTab;

    const handleTabChange = useCallback(
        (tab: ETab) => {
            setSearchParams({ [TAB_KEY]: TAB_TO_URL[tab] }, { replace: true });
        },
        [setSearchParams],
    );

    const [pauseModal, setPauseModal] = useState(false);
    const [resumeModal, setResumeModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const handlePause = useCallback(async () => {
        if (!detail) return;
        setActionLoading(true);
        try {
            const res = await updateGPListingStatus(detail.organizationId, { targetStatus: 'PAUSE_LISTED' });
            if (res && !res.error) {
                message.success(`"${detail.name}" has been paused.`);
                setPauseModal(false);
                handleTabChange(ETab.GROUP_PRACTICE);
                refetch();
            } else {
                message.error(res?.error || 'Failed to pause listing');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    }, [detail, refetch, handleTabChange]);

    const handleResume = useCallback(async () => {
        if (!detail) return;
        setActionLoading(true);
        try {
            const res = await updateGPListingStatus(detail.organizationId, { targetStatus: 'LISTED' });
            if (res && !res.error) {
                message.success(`"${detail.name}" has been re-listed.`);
                setResumeModal(false);
                handleTabChange(ETab.KLARITY_PROFILE);
                refetch();
            } else {
                message.error(res?.error || 'Failed to resume listing');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    }, [detail, refetch, handleTabChange]);

    if (loading) {
        return (
            <div className={s.wrap}>
                <div className={s.empty}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    if (!detail) {
        return (
            <div className={s.wrap}>
                <div className={s.empty}>Practice not found</div>
            </div>
        );
    }

    const statusItem = GP_STATUS_MAP[detail.listingStatus] || { color: 'default', label: detail.listingStatus };

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div className={s.headerInner}>
                    <div className={s.left}>
                        <h1 className={s.leftBtm}>
                            <span className={s.title}>{detail.name}</span>
                            <Tag color={statusItem.color}>{statusItem.label}</Tag>
                        </h1>
                    </div>
                </div>
            </div>
            <div className={s.tabNav}>
                <div
                    className={`${s.tabItem} ${currentTab === ETab.KLARITY_PROFILE ? s.tabItemActive : ''}`}
                    onClick={() => handleTabChange(ETab.KLARITY_PROFILE)}
                >
                    {ETab.KLARITY_PROFILE}
                </div>
                <div
                    className={`${s.tabItem} ${currentTab === ETab.GROUP_PRACTICE ? s.tabItemActive : ''}`}
                    onClick={() => handleTabChange(ETab.GROUP_PRACTICE)}
                >
                    {ETab.GROUP_PRACTICE}
                </div>
            </div>
            {currentTab === ETab.KLARITY_PROFILE && (
                <KlarityProfileTab
                    detail={detail}
                    profileLink={
                        detail.slugName ? `${getHelloKlarityHost()}/group/${detail.slugName}?isPreview=true` : ''
                    }
                />
            )}
            {currentTab === ETab.GROUP_PRACTICE && <GroupPracticeTab organizationId={detail.organizationId} />}
            <div className={s.footer}>
                <div className={s.footerLeft}>
                    <Button
                        className={s.footerBtn}
                        onClick={() => navigate('/operation/hello-klarity-providers?type=gp')}
                    >
                        Exit
                    </Button>
                </div>
                <div className={s.footerRight}>
                    {isListed && (
                        <Button type="link" className={s.pauseBtn} onClick={() => setPauseModal(true)}>
                            Pause Listing (Preferred)
                        </Button>
                    )}
                    {showResumeListing && (
                        <Button type="primary" className={s.publishBtn} onClick={() => setResumeModal(true)}>
                            Resume listing
                        </Button>
                    )}
                </div>
            </div>

            <Modal
                open={pauseModal}
                onCancel={() => setPauseModal(false)}
                title={`Pause listing ${detail.name}?`}
                closeIcon={<CloseOutlined />}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setPauseModal(false)}>Cancel</Button>
                        <Button type="primary" danger loading={actionLoading} onClick={handlePause}>
                            Pause
                        </Button>
                    </div>
                }
                destroyOnClose
            >
                <p style={{ fontSize: 14, color: '#374151' }}>
                    This practice will appear as &ldquo;not accepting new patients&rdquo; on HelloKlarity, and all
                    providers in this practice will temporarily stop receiving new patient bookings. You can re-list
                    anytime. All existing data will be preserved.
                </p>
            </Modal>

            <Modal
                open={resumeModal}
                onCancel={() => setResumeModal(false)}
                title={`Resume listing ${detail.name}?`}
                closeIcon={<CloseOutlined />}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setResumeModal(false)}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={actionLoading}
                            onClick={handleResume}
                            style={{ backgroundColor: '#16A34A', borderColor: '#16A34A' }}
                        >
                            Resume
                        </Button>
                    </div>
                }
                destroyOnClose
            >
                <p style={{ fontSize: 14, color: '#4B5563' }}>
                    This will restore the practice listing on Hello Klarity. Providers will need to be re-listed
                    individually if you want their profiles searchable too.
                </p>
            </Modal>
        </div>
    );
};

export default GroupPracticeDetailPage;
